import { useState, useRef, useEffect } from 'react';
import { sn, fmt$ } from '../utils/formatters';
import { matchTokensAny } from '../utils/search';
import { gu } from '../utils/conversions';

function fmtPack(ppc,spp,cq,cu){const q=ppc||1;const s=spp||cq;const u=cu||null;if(s!=null&&s!==0&&u)return `${q} × ${s} ${u}`;if(s!=null&&s!==0)return `${q} × ${s}`;if(u)return `${q} ${u}`;return `${q}`;}

export default function IngredientDropdown({ings,value,onSelect,onClose,placeholder,mode='rich',excludeIds,excludeCategories,minChars=0,maxResults,t,si,showAllOnEmpty=false}){
// mode='rich' (default): 4-col table (Name|Brand|Distributor|Case $), searches name+brand+dist
// mode='light': simple name list with type badge, searches name only
const[search,setSearch]=useState('');
const[open,setOpen]=useState(false);
const[hl,setHl]=useState(-1);
const[dropUp,setDropUp]=useState(false);
const ref=useRef(null);
const dropRef=useRef(null);

useEffect(()=>{
  function onKey(e){if(e.key==='Escape'){setOpen(false);if(onClose)onClose();}}
  function onClick(e){if(ref.current&&!ref.current.contains(e.target)){setOpen(false);if(onClose)onClose();}}
  document.addEventListener('keydown',onKey);
  document.addEventListener('mousedown',onClick);
  return()=>{document.removeEventListener('keydown',onKey);document.removeEventListener('mousedown',onClick);};
},[onClose]);

const excludeSet=new Set(excludeIds||[]);
const excludeCats=new Set(excludeCategories||[]);

const query=open?search:(value||'');
const hasQuery=query.trim().length>=Math.max(minChars,1);
const showList=open&&(hasQuery||showAllOnEmpty);

// Flip dropdown upward when not enough room below
useEffect(()=>{
  if(!showList||!ref.current)return;
  const rect=ref.current.getBoundingClientRect();
  const spaceBelow=window.innerHeight-rect.bottom;
  setDropUp(spaceBelow<250);
},[showList]);

const filtered=showList?(()=>{
  const tokens=query.toLowerCase().trim().split(/\s+/);
  const matches=(ings||[]).filter(ig=>
    ig.active!==false&&
    !excludeSet.has(ig.id)&&
    !excludeCats.has(ig.cat)&&
    (mode==='rich'
      ?matchTokensAny([sn(ig.n),ig.b||'',ig.dist||''],query)
      :matchTokensAny([sn(ig.n)],query))
  );
  // Tiered ranking: 1=name starts with token, 2=name contains token, 3=brand/dist only
  function tier(ig){
    const nl=sn(ig.n).toLowerCase();
    const nameWords=nl.split(/\s+/);
    if(tokens.every(tk=>nameWords.some(w=>w.startsWith(tk))))return 1;
    if(tokens.every(tk=>nl.includes(tk)))return 2;
    return 3;
  }
  return matches.sort((a,b)=>{
    const ta=tier(a),tb=tier(b);
    if(ta!==tb)return ta-tb;
    return sn(a.n).localeCompare(sn(b.n));
  }).slice(0,maxResults||9999);
})():[];

function handleKeyDown(e){
  if(!open||!showList)return;
  if(e.key==='ArrowDown'){e.preventDefault();setHl(p=>Math.min(p+1,filtered.length-1));}
  else if(e.key==='ArrowUp'){e.preventDefault();setHl(p=>Math.max(p-1,0));}
  else if(e.key==='Enter'){e.preventDefault();if(hl>=0&&hl<filtered.length){select(filtered[hl]);}else if(filtered.length===1){select(filtered[0]);}}
}

function select(ig){
  onSelect(ig);
  setSearch('');
  setOpen(false);
  setHl(-1);
}

return(
<div ref={ref} style={{position:'relative'}}>
<input
  value={open?search:(value||'')}
  onChange={e=>{setSearch(e.target.value);setOpen(true);setHl(-1);}}
  onFocus={()=>{setOpen(true);setSearch(value||'');setHl(-1);}}
  placeholder={placeholder||'Search...'}
  name="ingredient-lookup" autoComplete="chrome-off" data-1p-ignore data-lpignore="true"
  onKeyDown={handleKeyDown}
  style={{...si,width:'100%',fontSize:11,boxSizing:'border-box'}}
/>
{showList&&<div ref={dropRef} style={{position:'absolute',[dropUp?'bottom':'top']:'100%',left:0,right:0,background:t.bg,border:`2px solid ${t.bl}`,borderRadius:10,maxHeight:'50vh',overflow:'auto',zIndex:30,[dropUp?'marginBottom':'marginTop']:6,boxShadow:'0 12px 32px rgba(0,0,0,0.35), 0 4px 12px rgba(0,0,0,0.2)'}}>
{filtered.length===0?<div style={{padding:'8px 12px',fontSize:11,color:t.td,fontStyle:'italic'}}>No matches</div>:

mode==='rich'?<table style={{width:'100%',borderCollapse:'collapse'}}>
<thead><tr style={{background:t.sf,position:'sticky',top:0}}>
{['Name','Brand','Pack','Case $','Unit $'].map(h=><th key={h} style={{padding:'6px 10px',textAlign:h==='Case $'||h==='Unit $'?'right':'left',fontSize:8,color:t.td,textTransform:'uppercase',fontWeight:600,borderBottom:`1px solid ${t.bd}`}}>{h}</th>)}
</tr></thead>
<tbody>{filtered.map((ig,i)=>{const uc=gu(ig,ig.pcu||ig.cu);return(
<tr key={ig.id} onClick={()=>select(ig)} style={{cursor:'pointer',background:hl===i?t.sfh:'transparent',borderBottom:`1px solid ${t.bd}`}} onMouseEnter={()=>setHl(i)}>
<td style={{padding:'6px 10px',fontSize:12,fontWeight:500}}>{sn(ig.n)}</td>
<td style={{padding:'6px 10px',fontSize:11,color:t.tm}}>{ig.b||'—'}</td>
<td style={{padding:'6px 10px',fontSize:11,color:t.tm,whiteSpace:'nowrap'}}>{fmtPack(ig.ppc,ig.spp,ig.cq,ig.cu)}</td>
<td style={{padding:'6px 10px',fontSize:11,fontFamily:'monospace',fontWeight:500,textAlign:'right'}}>${fmt$(ig.cp||0)}</td>
<td style={{padding:'6px 10px',fontSize:11,fontFamily:'monospace',fontWeight:500,textAlign:'right',color:t.tm}}>{uc!=null?'$'+fmt$(uc):'—'}</td>
</tr>);})}</tbody>
</table>:

filtered.map((ig,i)=>
<div key={ig.id} onClick={()=>select(ig)} style={{padding:'7px 10px',cursor:'pointer',borderBottom:`1px solid ${t.bd}`,fontSize:12,background:hl===i?t.sfh:'transparent',display:'flex',justifyContent:'space-between',alignItems:'center'}} onMouseEnter={()=>setHl(i)}>
<span style={{fontWeight:500}}>{sn(ig.n)}</span>
{ig.type&&<span style={{fontSize:9,padding:'1px 5px',borderRadius:3,background:ig.type==='menu'?'rgba(59,130,246,0.1)':ig.type==='batch'?'rgba(212,168,36,0.1)':'rgba(139,143,158,0.1)',color:ig.type==='menu'?t.bl:ig.type==='batch'?t.yl:t.td}}>{ig.type==='menu'?'menu item':ig.type}</span>}
</div>)}

{!hasQuery&&showAllOnEmpty&&filtered.length>20&&<div style={{padding:'6px 10px',fontSize:10,color:t.td,textAlign:'center',borderTop:`1px solid ${t.bd}`}}>Type to narrow results</div>}
</div>}
{open&&!showList&&query.length>0&&query.length<minChars&&<div style={{position:'absolute',[dropUp?'bottom':'top']:'100%',left:0,right:0,background:t.sf,border:`1px solid ${t.bd}`,borderRadius:8,padding:'8px 12px',fontSize:10,color:t.td,zIndex:20,[dropUp?'marginBottom':'marginTop']:2}}>Type {minChars-query.length} more character{minChars-query.length!==1?'s':''} to search</div>}
</div>);
}
